import type { Session, SessionStorageBackend } from '@nhost/nhost-js/session'
import { SESSION_COOKIE, serializeSession, deserializeSession } from './session-cookie-codec'

/**
 * Browser session storage: same cookie-based approach as the SDK's official CookieStorage (so the
 * Next.js server can read the session for flash-free gating), but it stores the session WITHOUT the
 * redundant `decodedToken` and rebuilds it on read — keeping the cookie under the 4 KB browser limit
 * for multi-property admins. See lib/session-cookie-codec.ts for the full rationale.
 *
 * Attributes match the SDK's CookieStorage: path=/, 30-day expiry, SameSite=Lax, Secure in prod only
 * (a Secure cookie is never stored on http://localhost, which would break dev login).
 */
export class CookieSessionStorage implements SessionStorageBackend {
  private readonly cookieName = SESSION_COOKIE
  private readonly expirationDays = 30
  private readonly secure = process.env.NODE_ENV === 'production'

  get(): Session | null {
    if (typeof document === 'undefined') return null
    const prefix = `${this.cookieName}=`
    const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix))
    if (!entry) return null
    return deserializeSession(entry.slice(prefix.length))
  }

  set(value: Session): void {
    if (typeof document === 'undefined') return
    const expires = new Date(Date.now() + this.expirationDays * 24 * 60 * 60 * 1000).toUTCString()
    const secure = this.secure ? ' Secure;' : ''
    document.cookie = `${this.cookieName}=${serializeSession(value)}; expires=${expires}; path=/;${secure} SameSite=Lax`
  }

  remove(): void {
    if (typeof document === 'undefined') return
    document.cookie = `${this.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`
  }
}
